import { isEmpty } from 'lodash';
import { difference } from 'lodash/array';
import { setIn } from 'services/immutable';

export const migrateViews = (
  normalizedViews,
  defaultTableState,
  standardOrderedFields,
  columnGroupingModel,
  columns,
  tableVersion
) => {
  return Object.entries(normalizedViews).reduce((result, [key, view]) => {
    if (!view || view?.version === tableVersion) return { ...result, [key]: view };

    const removedColumns = difference(view.value.columns.orderedFields, standardOrderedFields);
    const newColumns = difference(standardOrderedFields, view.value.columns.orderedFields);

    // Process view if there are new columns
    const savedViewWithNewColumns = !isEmpty(newColumns)
      ? addNewColumns(view, newColumns, standardOrderedFields, columnGroupingModel, defaultTableState)
      : view;
    // Process view if there are removed columns
    const savedViewWithoutRemovedColumns = !isEmpty(removedColumns)
      ? removeDeletedColumns(savedViewWithNewColumns, removedColumns)
      : savedViewWithNewColumns;

    // Align saved dimensions with current column sizing config (e.g. minWidth/flex)
    const savedViewWithActualColumnDimensions = normalizeColumnDimensions(savedViewWithoutRemovedColumns, columns);

    // Process view if there are filter saved
    const savedViewWithoutOutdatedFilters = removeOutdatedFilters(savedViewWithActualColumnDimensions, columns);

    // Ensure quick filter includes hidden columns
    const savedViewWithQuickFilterIncludingHiddenColumns = quickFilterShouldIncludeHiddenColumns(
      savedViewWithoutOutdatedFilters
    );

    // Process view if there are sorting saved
    const savedViewWithoutOutdatedSorting = removeOutdatedSorting(
      savedViewWithQuickFilterIncludingHiddenColumns,
      standardOrderedFields
    );
    // Update view version
    const resultViewWithUpdatedVersion = {
      ...savedViewWithoutOutdatedSorting,
      version: tableVersion
    };

    return { ...result, [key]: resultViewWithUpdatedVersion };
  }, {});
};

const normalizeDimension = (dimension, columnConfig) => {
  const normalized = { ...dimension };

  const legacyFlex = typeof dimension.flex === 'number' && dimension.flex > 0;
  const currentFlex = typeof columnConfig.flex === 'number' && columnConfig.flex > 0;

  // legacy migration: preserve width once when moving from flex → fixed
  if (legacyFlex && !currentFlex && typeof dimension.minWidth === 'number') {
    normalized.width = dimension.minWidth;
  }

  if (typeof columnConfig.minWidth === 'number') {
    normalized.minWidth = columnConfig.minWidth;
  }

  if (currentFlex) {
    normalized.flex = columnConfig.flex;
  } else {
    delete normalized.flex;
  }

  return normalized;
};

export const normalizeColumnDimensions = (view, columns) => {
  const dimensions = view.value.columns?.dimensions;
  if (!dimensions) return view;

  const columnConfigMap = Object.fromEntries(columns.map(c => [c.field, c]));

  const normalizedDimensions = Object.entries(dimensions).reduce((acc, [field, dimension]) => {
    const columnConfig = columnConfigMap[field];
    if (!columnConfig) return acc;

    acc[field] = normalizeDimension(dimension, columnConfig);
    return acc;
  }, {});

  return setIn(view, 'value.columns.dimensions', normalizedDimensions);
};

export const removeOutdatedSorting = (view, standardOrderedFields) => {
  if (!view.value.sorting?.sortModel?.length) return view;

  const updatedSortModel = view.value.sorting.sortModel.filter(sortItem =>
    standardOrderedFields.some(col => col === sortItem.field)
  );

  return setIn(view, 'value.sorting.sortModel', updatedSortModel);
};

export const removeOutdatedFilters = (view, columns) => {
  if (!view.value.filter?.filterModel?.items?.length) return view;

  const updatedFilterItems = view.value.filter.filterModel.items.filter(filterItem => {
    const filterOperatorFromCurrentConfiguration = columns.find(col => col.field === filterItem.field)?.filterOperators;

    // Remove filter if column does not exist anymore
    if (!filterOperatorFromCurrentConfiguration) return false;

    // Remove filter if saved operator does not exist in the current configuration
    const savedOperatorIsValid = filterOperatorFromCurrentConfiguration?.some(op => op.value === filterItem.operator);
    return !isEmpty(filterOperatorFromCurrentConfiguration) && savedOperatorIsValid;
  });

  return setIn(view, 'value.filter.filterModel.items', updatedFilterItems);
};

export const quickFilterShouldIncludeHiddenColumns = view => {
  if (view.value.filter?.filterModel?.items?.length) {
    return setIn(view, 'value.filter.filterModel.quickFilterExcludeHiddenColumns', false);
  } else {
    const defaultFilterModel = setIn(view, 'value.filter.filterModel.items', []);
    return setIn(defaultFilterModel, 'value.filter.filterModel.quickFilterExcludeHiddenColumns', false);
  }
};

export const removeDeletedColumns = (view, removedColum) => {
  // Remove columns from visibility model
  const updatedVisibilityModel = Object.fromEntries(
    Object.entries(view.value.columns.columnVisibilityModel).filter(([key]) => !removedColum.includes(key))
  );
  const viewWithUpdatedVisibilityModel = setIn(view, 'value.columns.columnVisibilityModel', updatedVisibilityModel);
  // Remove columns from ordered fields
  const viewWithoutRemovedColumns = setIn(
    viewWithUpdatedVisibilityModel,
    'value.columns.orderedFields',
    viewWithUpdatedVisibilityModel.value.columns.orderedFields.filter(col => !removedColum.includes(col))
  );

  // Remove columns from pinned columns
  const viewWithoutPinnedRemovedColumns = setIn(viewWithoutRemovedColumns, 'value.pinnedColumns', {
    left: viewWithoutRemovedColumns.value.pinnedColumns.left?.filter(col => !removedColum.includes(col)),
    right: viewWithoutRemovedColumns.value.pinnedColumns.right?.filter(col => !removedColum.includes(col))
  });

  // Remove columns from dimensions model if exist
  return setIn(
    viewWithoutPinnedRemovedColumns,
    'value.columns.dimensions',
    Object.fromEntries(
      Object.entries(viewWithoutPinnedRemovedColumns.value.columns.dimensions).filter(
        ([key]) => !removedColum.includes(key)
      )
    )
  );
};

export const addNewColumns = (view, newColumns, standardOrderedFields, columnGroupingModel, defaultTableState) => {
  // Update visibility model
  const hiddenVisibilityForNewColumns = Object.fromEntries(newColumns.map(field => [field, false]));

  const viewWithVisibilityModelForNewColumns = setIn(view, 'value.columns.columnVisibilityModel', {
    ...defaultTableState.columns.columnVisibilityModel,
    ...view.value.columns.columnVisibilityModel,
    ...hiddenVisibilityForNewColumns
  });

  return newColumns.reduce((updatedView, newColumnName) => {
    if (!standardOrderedFields.includes(newColumnName)) return updatedView;

    const orderedFields = updatedView.value.columns.orderedFields;
    const previousExistingColumn = findPreviousExistingColumn(newColumnName, orderedFields, standardOrderedFields);
    if (previousExistingColumn) {
      return insertColumn(updatedView, newColumnName, previousExistingColumn, columnGroupingModel, true);
    }

    const nextExistingColumn = findNextExistingColumn(newColumnName, orderedFields, standardOrderedFields);
    if (nextExistingColumn) {
      return insertColumn(updatedView, newColumnName, nextExistingColumn, columnGroupingModel, false);
    }

    return insertColumn(updatedView, newColumnName, null, columnGroupingModel, true);
  }, viewWithVisibilityModelForNewColumns);
};

const findPreviousExistingColumn = (field, orderedFields, standardOrderedFields) => {
  const fieldIndex = standardOrderedFields.indexOf(field);

  for (let i = fieldIndex - 1; i >= 0; i--) {
    if (orderedFields.includes(standardOrderedFields[i])) {
      return standardOrderedFields[i];
    }
  }

  return null;
};

const findNextExistingColumn = (field, orderedFields, standardOrderedFields) => {
  const fieldIndex = standardOrderedFields.indexOf(field);

  for (let i = fieldIndex + 1; i < standardOrderedFields.length; i++) {
    if (orderedFields.includes(standardOrderedFields[i])) {
      return standardOrderedFields[i];
    }
  }

  return null;
};

export const insertColumn = (view, newColumnName, anchorColumnName, columnGroupingModel, after) => {
  const orderedFields = view.value.columns.orderedFields.filter(field => field !== newColumnName);
  const anchorColumnIndex = orderedFields.indexOf(anchorColumnName);
  const indexForNewColumn = anchorColumnIndex + (after ? 1 : 0);

  let newOrderedFields;
  if (anchorColumnIndex < 0) {
    // If anchor column is not found, add the new column at the end of the group or at the end of the list
    const groupId = getGroupId(newColumnName, columnGroupingModel);
    if (groupId) {
      const lastColumnInGroup = [...orderedFields]
        .reverse()
        .find(col => getGroupId(col, columnGroupingModel) === groupId);
      const lastColumnIndex = orderedFields.indexOf(lastColumnInGroup);
      if (lastColumnIndex !== -1) {
        // Insert after the last column in the group
        newOrderedFields = [
          ...orderedFields.slice(0, lastColumnIndex + 1),
          newColumnName,
          ...orderedFields.slice(lastColumnIndex + 1)
        ];
      } else {
        // If columns from group not founded - add to the end of the list
        newOrderedFields = [
          ...orderedFields.slice(0, orderedFields.length),
          newColumnName,
          ...orderedFields.slice(orderedFields.length)
        ];
      }
    } else {
      // If no group, add to the end of the list
      newOrderedFields = [
        ...orderedFields.slice(0, orderedFields.length),
        newColumnName,
        ...orderedFields.slice(orderedFields.length)
      ];
    }
  } else {
    newOrderedFields = [
      ...orderedFields.slice(0, indexForNewColumn),
      newColumnName,
      ...orderedFields.slice(indexForNewColumn)
    ];
  }

  const updatedView = setIn(view, 'value.columns.orderedFields', newOrderedFields);

  // Create a new view with pinned columns updated
  return updatePinnedColumns(updatedView, newColumnName, anchorColumnName, after);
};

export const updatePinnedColumns = (view, newColumnName, anchorColumnName, after) => {
  const pinnedColumns = { ...view.value.pinnedColumns };

  if (pinnedColumns.left?.includes(anchorColumnName)) {
    const left = [...pinnedColumns.left];
    const indexForNewPinnedColumn = left.indexOf(anchorColumnName);
    const insertIndex = after ? indexForNewPinnedColumn + 1 : indexForNewPinnedColumn;
    pinnedColumns.left = [...left.slice(0, insertIndex), newColumnName, ...left.slice(insertIndex)];
  }

  if (pinnedColumns.right?.includes(anchorColumnName)) {
    const right = [...pinnedColumns.right];
    const indexForNewPinnedColumn = right.indexOf(anchorColumnName);
    const insertIndex = after ? indexForNewPinnedColumn + 1 : indexForNewPinnedColumn;
    pinnedColumns.right = [...right.slice(0, insertIndex), newColumnName, ...right.slice(insertIndex)];
  }

  return setIn(view, 'value.pinnedColumns', pinnedColumns);
};

export const getGroupId = (column, columnGroupingModel) =>
  columnGroupingModel?.find(group => group.children.some(c => c.field === column))?.groupId;
